import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

import { useStoreState } from "store/store";

import { Cart } from "../store/types";

/**
 * Ensures that the user cannot jump ahead in the booking flow by
 * verifying that the data from previous steps exists. When the required
 * data is missing the hook redirects to the last valid step.
 *
 * @param step Current step number (1 based)
 */
export const useStepGuard = (step: number) => {
  const cart = useStoreState((s) => s.cart);
  const isCustomDestination = useStoreState((s) => s.isCustomDestination);
  const isGlobalLoading = useStoreState((s) => s.isGlobalLoading);
  const bookingId = useStoreState((s) => s.bookingId);
  const navigate = useNavigate();
  // This is an ENTRY guard — per the docblock above, its job is to stop someone
  // jumping ahead to a step whose prerequisites were never filled in. It is not
  // meant to police the cart for the whole time the user sits on the step.
  //
  // It was doing the latter, because the effect depends on `cart` by object
  // identity and flattenCart() returns a fresh object for every response. So
  // any cart response landing mid-step re-ran the checks, and a response that
  // was momentarily missing location/destination redirected the user away —
  // unmounting every shouldUnregister field and destroying their input.
  //
  // Latching after the first evaluation against a hydrated cart keeps the
  // anti-jump-ahead behaviour exactly as it was (a direct load of /step-3 with
  // no cart still redirects, and re-entering the step remounts and re-arms
  // this) while making it impossible for a late response to eject someone
  // mid-typing. Loosening only ever removes redirects, so it cannot strand a
  // user on a step they reached legitimately.
  const settledRef = useRef(false);

  useEffect(() => {
    if (isGlobalLoading) return;
    if (settledRef.current) return;
    // `cart` is null until the first load resolves. Leaving the latch open
    // means "not loaded yet" is never mistaken for "prerequisites missing".
    if (cart) settledRef.current = true;

    const redirect = (target: string, reason: string) => {
      // eslint-disable-next-line no-console
      console.log(`[sjd-step-guard] step ${step} → ${target}`, {
        reason,
        cartLocation: cart?.location,
        cartDestination: cart?.destination,
        cartVehicle: cart?.vehicle,
        cartFirstname: cart?.firstname,
        cartEmail: cart?.email,
        cartBookingDate: cart?.booking_date,
        cartDepartureDate: cart?.departure_date,
        isCustomDestination,
      });
      navigate(target, { replace: true });
    };

    switch (step) {
      case 2:
        if (!cart?.location || (!cart?.destination && !isCustomDestination)) {
          redirect(
            "/step-1",
            !cart?.location ? "missing location" : "missing destination"
          );
        }
        break;
      case 3:
        if (!cart?.location || (!cart?.destination && !isCustomDestination)) {
          redirect("/step-1", "missing location/destination");
        } else if (!cart?.vehicle) {
          redirect("/step-2", "missing vehicle");
        }
        break;
      case 4:
      case 5:
        if (!cart?.location || (!cart?.destination && !isCustomDestination)) {
          redirect("/step-1", "missing location/destination");
        } else if (!cart?.vehicle) {
          redirect("/step-2", "missing vehicle");
        } else if (
          !cart?.booking_date &&
          !cart?.booking_time &&
          !cart?.departure_date &&
          !cart?.departure_time
        ) {
          redirect("/step-3", "missing booking/departure date+time");
        }
        break;
      case 6:
        if (!cart?.location || (!cart?.destination && !isCustomDestination)) {
          redirect("/step-1", "missing location/destination");
        } else if (!cart?.vehicle) {
          redirect("/step-2", "missing vehicle");
        } else if (
          !cart?.booking_date &&
          !cart?.booking_time &&
          !cart?.departure_date &&
          !cart?.departure_time
        ) {
          redirect("/step-3", "missing booking/departure date+time");
        } else if (!cart?.firstname || !cart?.lastname || !cart?.email) {
          redirect("/step-5", "missing contact info");
        }
        break;
      case 7: {
        // Parse the cookie inline (previously routed through setState then read
        // the still-stale previous value on the next line — classic closure bug,
        // plus the effect never re-fired because cartFinalResult wasn't in the
        // deps). Using a local gives us the value synchronously and the guards
        // actually fire.
        const savedCart = Cookies.get("cartFinalResult");
        if (!savedCart) break;

        let finalCart: Cart | undefined;
        try {
          finalCart = JSON.parse(savedCart) as Cart;
        } catch (error) {
          console.error("Error parsing cart from cookie:", error);
          break;
        }
        if (!finalCart) break;

        if (
          !finalCart.location ||
          (!finalCart.destination && !finalCart.is_custom_location)
        ) {
          redirect(
            "/step-1",
            "step-7 cookie cart missing location/destination"
          );
        } else if (!finalCart.vehicle) {
          redirect("/step-2", "step-7 cookie cart missing vehicle");
        } else if (
          !finalCart.booking_date &&
          !finalCart.booking_time &&
          !finalCart.departure_date &&
          !finalCart.departure_time
        ) {
          redirect("/step-3", "step-7 cookie cart missing date/time");
        } else if (
          !finalCart.firstname ||
          !finalCart.lastname ||
          !finalCart.email
        ) {
          redirect("/step-5", "step-7 cookie cart missing contact info");
        } else if (!finalCart.booking_id) {
          redirect("/step-6", "step-7 cookie cart missing booking_id");
        }
        break;
      }
      default:
        break;
    }
  }, [bookingId, cart, isCustomDestination, isGlobalLoading, navigate, step]);
};

export default useStepGuard;
